import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Save, Loader2, UserPlus, ShieldCheck } from 'lucide-react';
import { RecruiterResponse } from '../Types/RecruiterTypes';
import { updateRecruiterByCompanyAdmin } from '../Services/RecruiterService';
import { inviteRecruiterUser } from '../../App/Services/UserService';

interface Props {
  isOpen: boolean;
  mode: 'create' | 'edit';
  recruiter: RecruiterResponse | null;
  currentUserId: string;
  onClose: () => void;
  onSaved: (message: string) => void;
}

interface FormState {
  recruiterFirstName: string;
  recruiterLastName: string;
  recruiterEmail: string;
  designation: string;
  isCompanyAdminUser: boolean;
  recruiterStatus: boolean;
}

const emptyForm: FormState = {
  recruiterFirstName: '',
  recruiterLastName: '',
  recruiterEmail: '',
  designation: '',
  isCompanyAdminUser: false,
  recruiterStatus: true,
};

const fieldClass =
  'w-full rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 placeholder-slate-600 outline-none transition [color-scheme:dark] focus:border-cyan-500/50';

const labelClass = 'mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-400';

const RecruiterFormModal = ({
  isOpen,
  mode,
  recruiter,
  currentUserId,
  onClose,
  onSaved,
}: Props) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!isOpen) return;

    setFormError('');
    setSaving(false);

    if (mode === 'edit' && recruiter) {
      setForm({
        recruiterFirstName: recruiter.recruiterFirstName ?? '',
        recruiterLastName: recruiter.recruiterLastName ?? '',
        recruiterEmail: recruiter.recruiterEmail ?? '',
        designation: recruiter.designation ?? '',
        isCompanyAdminUser: recruiter.isCompanyAdminUser ?? false,
        recruiterStatus: recruiter.recruiterStatus ?? true,
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, mode, recruiter]);

  const title = useMemo(() => (mode === 'create' ? 'Invite Recruiter' : 'Edit Recruiter'), [mode]);

  const handleSubmit = async () => {
    if (!form.recruiterFirstName.trim() || !form.recruiterLastName.trim()) {
      setFormError('First name and last name are required.');
      return;
    }

    if (!form.recruiterEmail.trim()) {
      setFormError('Recruiter email is required.');
      return;
    }

    setSaving(true);
    setFormError('');

    try {
      let response;
      if (mode === 'create') {
        response = await inviteRecruiterUser({
          userId: currentUserId,
          recruiterFirstName: form.recruiterFirstName.trim(),
          recruiterLastName: form.recruiterLastName.trim(),
          recruiterEmail: form.recruiterEmail.trim(),
        });
      } else {
        response = await updateRecruiterByCompanyAdmin(recruiter!.userId, {
          userId: recruiter!.userId,
          companyId: recruiter!.companyId,
          recruiterFirstName: form.recruiterFirstName.trim(),
          recruiterLastName: form.recruiterLastName.trim(),
          recruiterEmail: form.recruiterEmail.trim(),
          designation: form.designation,
          isCompanyAdminUser: form.isCompanyAdminUser,
          recruiterStatus: form.recruiterStatus,
        });
      }

      if (response.success) {
        onSaved(
          mode === 'create' ? 'Invitation sent to the recruiter.' : 'Recruiter updated successfully.'
        );
        onClose();
      } else {
        setFormError(response.message || 'Could not save the recruiter.');
      }
    } catch (err) {
      console.error('Failed to save recruiter', err);
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/70 p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
            className="my-8 w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                  <UserPlus size={18} />
                </span>
                <div>
                  <h2 className="text-base font-black tracking-tight text-slate-100">{title}</h2>
                  <p className="text-[11px] font-semibold text-slate-500">
                    {mode === 'create'
                      ? 'Send an invite to a new team member'
                      : 'Update the recruiter details and access'}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-slate-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="space-y-4 px-6 py-5">
              {formError && (
                <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-xs font-bold text-rose-300">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>First Name *</label>
                  <input
                    type="text"
                    value={form.recruiterFirstName}
                    onChange={(e) => set('recruiterFirstName', e.target.value)}
                    placeholder="e.g. Swapnil"
                    className={fieldClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Last Name *</label>
                  <input
                    type="text"
                    value={form.recruiterLastName}
                    onChange={(e) => set('recruiterLastName', e.target.value)}
                    placeholder="e.g. Kamble"
                    className={fieldClass}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Email *</label>
                  <input
                    type="email"
                    value={form.recruiterEmail}
                    onChange={(e) => set('recruiterEmail', e.target.value)}
                    placeholder="name@company.com"
                    className={fieldClass}
                  />
                </div>

                {mode === 'edit' && (
                  <>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Designation</label>
                      <input
                        type="text"
                        value={form.designation}
                        onChange={(e) => set('designation', e.target.value)}
                        placeholder="e.g. Sr. Technical Recruiter"
                        className={fieldClass}
                      />
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={16} className="text-amber-400" />
                        <div>
                          <p className="text-xs font-black uppercase tracking-widest text-slate-300">
                            Company Admin
                          </p>
                          <p className="text-[11px] text-slate-500">Grant billing & team access</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => set('isCompanyAdminUser', !form.isCompanyAdminUser)}
                        className={`relative h-6 w-11 rounded-full transition ${
                          form.isCompanyAdminUser ? 'bg-amber-500' : 'bg-slate-700'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                            form.isCompanyAdminUser ? 'left-[22px]' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950 px-4 py-3">
                      <div>
                        <p className="text-xs font-black uppercase tracking-widest text-slate-300">Status</p>
                        <p className="text-[11px] text-slate-500">
                          {form.recruiterStatus ? 'Active recruiter' : 'Access disabled'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => set('recruiterStatus', !form.recruiterStatus)}
                        className={`relative h-6 w-11 rounded-full transition ${
                          form.recruiterStatus ? 'bg-emerald-500' : 'bg-slate-700'
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${
                            form.recruiterStatus ? 'left-[22px]' : 'left-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 rounded-xl bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-widest text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
              >
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                {saving ? 'Saving' : mode === 'create' ? 'Send Invite' : 'Save Changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RecruiterFormModal;
